import { useState, useEffect } from 'react';
import { notificationsApi } from '@/api/notifications';

const STAFF_EVENTS = [
  { key: 'STAGE_ADVANCED', label: 'Stage advanced' },
  { key: 'INQUIRY_CREATED', label: 'New inquiry' },
  { key: 'APPOINTMENT_ARRIVED', label: 'Customer arrived' },
  { key: 'PART_REQUEST_SUBMITTED', label: 'Part request' },
  { key: 'JOB_ASSIGNED', label: 'Job assigned to you' },
  { key: 'NOTE_REPLIED', label: 'Reply to your note' },
  { key: 'PAYMENT_RECEIVED', label: 'Payment received' },
  { key: 'HANDOVER_REJECTED', label: 'Handover rejected' },
] as const;

interface Props {
  soundEnabled: boolean;
  onSoundChange: (enabled: boolean) => void;
  onClose: () => void;
}

export default function NotificationPreferences({ soundEnabled, onSoundChange, onClose }: Props) {
  const [mutedEvents, setMutedEvents] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    notificationsApi
      .getPreferences()
      .then((res) => {
        setMutedEvents(res.data.mutedEvents);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const toggleEvent = async (event: string) => {
    const newMuted = mutedEvents.includes(event)
      ? mutedEvents.filter((e) => e !== event)
      : [...mutedEvents, event];
    setMutedEvents(newMuted);
    setSaving(true);
    try {
      await notificationsApi.updatePreferences({ mutedEvents: newMuted });
    } catch {
      // Revert on failure
      setMutedEvents(mutedEvents);
    } finally {
      setSaving(false);
    }
  };

  const toggleSound = async () => {
    const newValue = !soundEnabled;
    onSoundChange(newValue);
    try {
      await notificationsApi.updatePreferences({ soundEnabled: newValue });
    } catch {
      onSoundChange(!newValue);
    }
  };

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <button onClick={onClose} className="text-xs text-blue-600 hover:underline">
          &larr; Back
        </button>
        <span className="text-sm font-semibold text-foreground">Preferences</span>
        <span className="w-10" />
      </div>

      <div className="px-4 py-3 space-y-4">
        {/* Sound toggle */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-foreground">Notification sound</p>
            <p className="text-xs text-muted-foreground">Play a chime for new notifications</p>
          </div>
          <button
            onClick={toggleSound}
            className={`relative w-10 h-6 rounded-full transition-colors ${
              soundEnabled ? 'bg-blue-600' : 'bg-muted'
            }`}
            aria-label="Toggle sound"
          >
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                soundEnabled ? 'translate-x-4' : ''
              }`}
            />
          </button>
        </div>

        {/* Muted events */}
        <div>
          <p className="text-sm font-medium text-foreground mb-2">Notification types</p>
          <p className="text-xs text-muted-foreground mb-3">
            Turn off events you don&apos;t want to receive.
          </p>

          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-8 bg-muted rounded animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="space-y-1">
              {STAFF_EVENTS.map((evt) => {
                const enabled = !mutedEvents.includes(evt.key);
                return (
                  <button
                    key={evt.key}
                    onClick={() => toggleEvent(evt.key)}
                    disabled={saving}
                    className="flex items-center justify-between w-full px-3 py-2 rounded-lg hover:bg-accent transition-colors text-left"
                  >
                    <span className="text-sm text-foreground">{evt.label}</span>
                    <span
                      className={`w-4 h-4 rounded border flex items-center justify-center ${
                        enabled ? 'bg-blue-600 border-blue-600' : 'border-muted-foreground/40'
                      }`}
                    >
                      {enabled && (
                        <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                          <path
                            d="M1 4L3.5 6.5L9 1"
                            stroke="white"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
